/**
 * On-demand rendering: skip the GPU frame when nothing on screen can have changed.
 *
 * PlayCanvas keeps ticking `update()` every animation frame while `app.autoRender` is false; only
 * `render()` is skipped unless `app.renderNextFrame` is set. Streaming, LOD selection, scripts and
 * camera controllers therefore keep running — the only question each frame is "would a render
 * show something new?". This module answers it conservatively:
 *
 * - Anything that can animate without moving the camera makes the scene ineligible up front
 *   (`continuousRenderReasons`): weather, audio-reactive, particles, video/GIF/animated
 *   hotspots and portals, 4DGS frame sequences, animated HTML meshes, entity animations, custom
 *   scripts, model animations. Those scenes render every frame exactly as before.
 * - At runtime a frame renders when there was input recently, the camera pose changed, a tour is
 *   playing, LOD data is still streaming (or the engine asked via `frame:request`), any script
 *   instance / particle system / anim component is enabled, any node moved since the last render,
 *   or an explicit request came in (resize, capture, perf-mode toggle).
 * - After the last activity a short grace period keeps rendering (damping tails), and while idle
 *   a heartbeat still renders once a second so a missed signal can never freeze the picture for
 *   longer than that.
 *
 * Measured motivation: SuperSplat's viewer stops drawing when idle; on the iPhone profile the
 * StorySplat viewer rendered ~120 frames/s of identical images while nobody touched the screen.
 */

import type { OnDemandRenderStats, ViewerInstance } from '../types';
export type { OnDemandRenderStats } from '../types';

export interface RenderActivitySource {
    name: string;
    /** True while this source needs continuous rendering. Called once per frame; keep it cheap. */
    active: () => boolean;
}

export interface OnDemandRenderSchedulerOptions {
    /** Keep rendering this long after the last activity (ms). Covers camera damping tails. */
    graceMs?: number;
    /** While idle, still render one frame at least this often (ms). Safety net, ~1 fps. */
    heartbeatMs?: number;
    /** Clock, injectable for tests. */
    now?: () => number;
}

export interface RenderDecision {
    render: boolean;
    reason: string;
}

/** URL diagnostics override scene defaults, but never the editor's continuous loop. */
export function resolveOnDemandRendering(editor: boolean, configured?: boolean, override?: string | null): boolean {
    return !editor && override !== 'always' && (override === 'ondemand' || configured !== false);
}

/** Retain the latest API setting both before Start and while the real viewer is loading. */
export class DeferredOnDemandRendering {
    private target: Pick<ViewerInstance, 'setOnDemandRendering'> | null = null;
    private pending: boolean | undefined;

    set(enabled: boolean): void {
        if (this.target) this.target.setOnDemandRendering(enabled);
        else this.pending = enabled;
    }

    bind(target: Pick<ViewerInstance, 'setOnDemandRendering'>): void {
        this.target = target;
        if (this.pending !== undefined) target.setOnDemandRendering(this.pending);
        this.pending = undefined;
    }
}

export const ON_DEMAND_DEFAULT_GRACE_MS = 400;
export const ON_DEMAND_DEFAULT_HEARTBEAT_MS = 1000;

/** Pure decision logic: no engine dependency so it can be unit-tested with a fake clock. */
export class OnDemandRenderScheduler {
    private readonly graceMs: number;
    private readonly heartbeatMs: number;
    private readonly now: () => number;
    private readonly sources: RenderActivitySource[] = [];
    private lastInputAt = -Infinity;
    private lastActiveAt = -Infinity;
    private lastRenderAt = -Infinity;
    private pendingRequest: string | null = null;
    readonly stats: OnDemandRenderStats = { decided: 0, rendered: 0, skipped: 0, heartbeats: 0, lastReason: 'init', reasons: {} };

    constructor(options: OnDemandRenderSchedulerOptions = {}) {
        this.graceMs = options.graceMs ?? ON_DEMAND_DEFAULT_GRACE_MS;
        this.heartbeatMs = options.heartbeatMs ?? ON_DEMAND_DEFAULT_HEARTBEAT_MS;
        this.now = options.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    }

    /** Pointer / touch / wheel / keyboard / gamepad activity. */
    noteInput(): void {
        this.lastInputAt = this.now();
    }

    /** One-shot: render the next frame (resize, capture, streaming data, param change). */
    requestFrame(reason = 'requested'): void {
        this.pendingRequest = reason;
    }

    addSource(source: RenderActivitySource): () => void {
        this.sources.push(source);
        return () => {
            const i = this.sources.indexOf(source);
            if (i >= 0) this.sources.splice(i, 1);
        };
    }

    /**
     * Decide whether the frame that is about to be rendered is needed. Call once per tick, after
     * all updates ran (PlayCanvas `framerender`), and set `app.renderNextFrame` from the result.
     * `frameChanged` carries the names of per-frame signals the caller already evaluated (camera
     * moved, node transforms dirty, …); an empty array means none fired.
     */
    decide(frameChanged: readonly string[] = []): RenderDecision {
        const now = this.now();
        this.stats.decided += 1;
        let reason: string | null = null;

        if (now - this.lastInputAt < this.graceMs) reason = 'input';
        if (!reason && frameChanged.length > 0) reason = frameChanged[0];
        if (!reason) {
            for (const source of this.sources) {
                let active = false;
                try { active = source.active(); } catch { active = true; } // a broken probe must not freeze the scene
                if (active) { reason = source.name; break; }
            }
        }
        if (reason) this.lastActiveAt = now;

        if (!reason && this.pendingRequest) reason = this.pendingRequest;
        if (!reason && now - this.lastActiveAt < this.graceMs) reason = 'grace';
        if (!reason && now - this.lastRenderAt >= this.heartbeatMs) {
            reason = 'heartbeat';
            this.stats.heartbeats += 1;
        }

        this.pendingRequest = null;
        if (reason) {
            this.lastRenderAt = now;
            this.stats.rendered += 1;
            this.stats.lastReason = reason;
            const key = reason.length > 64 ? reason.slice(0, 64) : reason;
            if (key in this.stats.reasons || Object.keys(this.stats.reasons).length < 32) this.stats.reasons[key] = (this.stats.reasons[key] ?? 0) + 1;
            return { render: true, reason };
        }
        this.stats.skipped += 1;
        this.stats.lastReason = 'idle';
        return { render: false, reason: 'idle' };
    }
}

// ---------------------------------------------------------------------------------------------
// Scene eligibility: features that animate on their own keep the classic every-frame loop.

type Rec = Record<string, unknown>;
const asRec = (v: unknown): Rec | null => (v && typeof v === 'object' ? (v as Rec) : null);
const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const isMediaAnimated = (url: unknown, type: unknown): boolean => {
    const t = typeof type === 'string' ? type.toLowerCase() : '';
    if (t === 'video' || t === 'gif') return true;
    const u = typeof url === 'string' ? url.toLowerCase().split('?')[0] : '';
    return /\.(mp4|webm|mov|m4v|ogv|gif)$/.test(u);
};

/**
 * Returns the reasons a scene must keep rendering every frame, or an empty array when on-demand
 * rendering is safe. Reads the resolved viewer config (and the raw scene for fields that only
 * live there); every check is defensive so an unexpected shape counts as "keep rendering".
 */
export function continuousRenderReasons(config: unknown, scene?: unknown): string[] {
    const reasons: string[] = [];
    const c = asRec(config) ?? {};
    const s = asRec(scene) ?? {};
    try {
        const weather = asRec(c.weather);
        if (weather && weather.enabled !== false && (weather.type || weather.preset || weather.mode)) reasons.push('weather');
        const audio = asRec(c.audioReactive);
        if (audio && audio.enabled !== false && Object.keys(audio).length > 0) reasons.push('audio-reactive');
        if (asArr(c.particles).length > 0) reasons.push('particles');
        // Water is not a scene-wide reason: the water system is a render source while an animated
        // plane is on screen, so a scene with water still idles when the water is out of view.
        const frames = asRec(c.frameSequence) ?? asRec(s.frameSequence);
        if (frames && asArr(frames.frameUrls).length > 0) reasons.push('frame-sequence');
        for (const h of asArr(c.hotspots)) {
            const hs = asRec(h); if (!hs) continue;
            if (hs.opacityMode === 'animated' || isMediaAnimated(hs.mediaUrl ?? hs.imageUrl ?? hs.url, hs.type)) { reasons.push('animated-hotspot'); break; }
        }
        for (const p of asArr(c.portals)) {
            const pt = asRec(p); if (!pt) continue;
            if (isMediaAnimated(pt.mediaUrl ?? pt.thumbnailUrl ?? pt.url, pt.type ?? pt.mediaType ?? pt.contentType)) { reasons.push('animated-portal'); break; }
        }
        for (const m of asArr(c.htmlMeshes)) {
            const hm = asRec(m); if (!hm) continue;
            if (hm.animated === true || typeof hm.iframeUrl === 'string' || (typeof hm.updateRate === 'number' && hm.updateRate > 0)) { reasons.push('animated-html-mesh'); break; }
        }
        if (asArr(c.entityAnimations).length > 0) reasons.push('entity-animations');
        for (const m of asArr(c.customMeshes)) {
            const cm = asRec(m); if (!cm) continue;
            const interaction = asRec(cm.interaction);
            if (interaction?.animationAutoPlay === true || interaction?.playModelAnimation === true || cm.animationAutoPlay === true || cm.playModelAnimation === true || asArr(cm.animations).length > 0) { reasons.push('model-animation'); break; }
        }
        const customScript = c.customScript ?? s.customScript;
        if ((typeof customScript === 'string' && customScript.trim() !== '') || asArr(c.customScripts ?? s.customScripts).length > 0) reasons.push('custom-scripts');
    } catch {
        reasons.push('config-unreadable');
    }
    return reasons;
}

// ---------------------------------------------------------------------------------------------
// Engine probes. Typed loosely on purpose: they read private-ish PlayCanvas fields and must
// degrade to "render" (return true) if the shape ever changes.

interface NodeLike {
    name?: string;
    /** Hotspot / portal video textures attach their element here (see createViewer). */
    videoElement?: { paused?: boolean; ended?: boolean } | null;
    _dirtyWorld?: boolean;
    _dirtyLocal?: boolean;
    enabled?: boolean;
    children?: NodeLike[];
    script?: { enabled?: boolean; scripts?: Array<{ enabled?: boolean; __scriptType?: { __name?: string }; constructor?: { __name?: string; name?: string } }> } | null;
    particlesystem?: { enabled?: boolean; isPlaying?: () => boolean } | null;
    anim?: { enabled?: boolean; playing?: boolean } | null;
    animation?: { enabled?: boolean; playing?: boolean } | null;
}

/**
 * Effect scripts that stay enabled for the life of the scene but only change the image when
 * something else changes (a light entity moves, a clip plane is set). Their per-frame update is
 * parameter sync, not animation, so they must not keep on-demand rendering awake. Anything not
 * listed (reveal, dissolve/wipe transitions, weather, audio-reactive, shader effects, user
 * scripts) counts as animating.
 */
export const STATIC_EFFECT_SCRIPTS: ReadonlySet<string> = new Set(['gsplatRelighting', 'gsplatMirrorClip', 'gsplatCompareClip']);

const scriptTypeName = (instance: { __scriptType?: { __name?: string }; constructor?: { __name?: string; name?: string } } | undefined): string => {
    const name = instance?.__scriptType?.__name ?? instance?.constructor?.__name ?? instance?.constructor?.name;
    return name && name !== 'Object' ? name : '?';
};

/**
 * True when any node under `root` moved since the last render (transform dirty flags are only
 * cleared by the render's hierarchy sync), or has an enabled script instance, a running particle
 * system, or a playing anim component. One cheap traversal per frame.
 */
export function sceneGraphNeedsRender(root: NodeLike | null | undefined): string | null {
    if (!root) return null;
    const stack: NodeLike[] = [root];
    let guard = 0;
    while (stack.length) {
        const node = stack.pop()!;
        if (++guard > 200_000) return 'graph-too-large';
        // A disabled subtree is not drawn, and the engine's hierarchy sync skips it, so its dirty
        // flags stay set forever (a hidden cursor ring did exactly that). Ignore it entirely.
        if (node.enabled === false) continue;
        if (node._dirtyWorld || node._dirtyLocal) return `transform-changed:${node.name ?? '?'}`;
        const script = node.script;
        if (script && script.enabled !== false && script.scripts) {
            for (const instance of script.scripts) {
                if (!instance || instance.enabled === false) continue;
                const name = scriptTypeName(instance);
                if (STATIC_EFFECT_SCRIPTS.has(name)) continue;
                return `script-active:${node.name ?? '?'}:${name}`;
            }
        }
        const ps = node.particlesystem;
        if (ps && ps.enabled !== false) return 'particles-active';
        const anim = node.anim;
        if (anim && anim.enabled !== false && anim.playing === true) return 'anim-playing';
        const animation = node.animation;
        if (animation && animation.enabled !== false && animation.playing === true) return 'animation-playing';
        // A playing video repaints its texture without moving anything, so no other probe would
        // catch it. The config gate normally keeps such scenes continuous, but it matches on
        // config field names and URL shapes; this is the authoritative check.
        const video = node.videoElement;
        if (video && video.paused === false && video.ended !== true) return `video-playing:${node.name ?? '?'}`;
        const children = node.children;
        if (children) for (let i = 0; i < children.length; i++) stack.push(children[i]);
    }
    return null;
}

interface CameraLike {
    getPosition: () => { x: number; y: number; z: number };
    getRotation: () => { x: number; y: number; z: number; w: number };
    camera?: { fov?: number; aspectRatio?: number; orthoHeight?: number; projection?: number; nearClip?: number; farClip?: number; horizontalFov?: boolean } | null;
}

/** Tracks the camera pose between frames; returns true when it changed beyond an epsilon. */
export class CameraPoseTracker {
    private last: number[] | null = null;
    constructor(private readonly epsilon = 1e-5) {}
    changed(camera: CameraLike | null | undefined, width: number, height: number): boolean {
        if (!camera) return false;
        const p = camera.getPosition(); const r = camera.getRotation(); const c = camera.camera ?? {};
        const cur = [p.x, p.y, p.z, r.x, r.y, r.z, r.w, c.fov ?? 0, c.aspectRatio ?? 0, c.orthoHeight ?? 0, c.projection ?? 0, c.nearClip ?? 0, c.farClip ?? 0, Number(c.horizontalFov ?? false), width, height];
        const last = this.last;
        if (!last || cur.some((value, i) => Math.abs(value - last[i]) > this.epsilon)) {
            // Keep the last reported pose, not the last sampled pose: slow movement must
            // accumulate instead of being discarded as sub-epsilon on every update.
            this.last = cur;
            return true;
        }
        return false;
    }
    reset(): void { this.last = null; }
}
